import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Sparkles, Zap, Video, ArrowRight, Eye, Wand2, Box, Film, Layers, Target, Palette } from 'lucide-react';
import ImageCollage from '../components/content/ImageCollage';
import { Button } from '../components/ui/button';

// Video Collage Component for vertical videos
const VideoCollage: React.FC<{ videoUrls: string[]; title: string }> = ({ videoUrls, title }) => {
  return (
    <div className="w-full h-full flex gap-2 p-2">
      {videoUrls.map((videoUrl, index) => (
        <div key={index} className="flex-1 h-full rounded-xl overflow-hidden">
          <video
            src={videoUrl}
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
            style={{ aspectRatio: '9/16' }} // Vertical video aspect ratio
          />
        </div>
      ))}
    </div>
  );
};

const services = [
  {
    title: 'Image Studio',
    subtitle: 'AI Image Creation',
    description: 'AI-powered image generation and enhancement',
    features: ['AI Generation', 'Enhancement', 'Brand Kit'],
    link: '/app/content-hub/image-studio',
    icon: Wand2,
    gradient: 'from-indigo-500 via-purple-500 to-pink-500',
    glowColor: 'shadow-indigo-500/25',
    isCollage: true,
    imageUrls: [
      '/images/Ad Studio/image1.png',
      '/images/Ad Studio/image2.jpeg',
      '/images/Ad Studio/image3.jpeg',
    ]
  },
  {
    title: '3D Studio',
    subtitle: '3D Model Creation',
    description: 'Create 3D models from images',
    features: ['3D Models', 'Model Export', 'Interactive'],
    link: '/app/content-hub/3d-studio',
    icon: Box,
    gradient: 'from-blue-500 via-purple-500 to-blue-500',
    glowColor: 'shadow-emerald-500/25',
    isCollage: true,
    imageUrls: [
      '/images/ContentCard/3d-1.jpg',
      '/images/ContentCard/3d-2.jpg',
      '/images/ContentCard/3d-3.jpg',
      '/images/ContentCard/3d-4.png',
    ]
  },
  {
    title: 'AR Studio',
    subtitle: 'Augmented Reality',
    description: 'Create AR experiences from 3D models',
    features: ['AR visuals', 'Interactive', 'Mobile Ready'],
    link: '/app/content-hub/ar-studio',
    icon: Layers,
    gradient: 'from-cyan-500 via-blue-500 to-indigo-500',
    glowColor: 'shadow-cyan-500/25',
    isCollage: true,
    imageUrls: [
      '/images/ContentCard/image1.png',
      '/images/ContentCard/image2.png',
      '/images/ContentCard/image3.png',
      '/images/ContentCard/image4.png',
    ]
  },
  {
    title: 'Video Studio',
    subtitle: 'Video Production',
    description: 'AI-powered video editing and effects',
    features: ['Video Generation', 'Smart Editing', 'Effects'],
    link: '/app/content-hub/video-studio',
    icon: Film,
    gradient: 'from-rose-500 via-pink-500 to-purple-500',
    glowColor: 'shadow-rose-500/25',
    isVideo: true,
    videoUrls: [
      '/videos/video1.mp4',
      '/videos/video2.mp4',
    ]
  },
];

const inspirationImages = [
  '/images/inspiration/img1.jpg',
  '/images/inspiration/img2.png',
  '/images/inspiration/img4.png',
  '/images/inspiration/img5.png',
  '/images/inspiration/img6.png',
  '/images/inspiration/img7.png',
  '/images/inspiration/img8.png',
  '/images/inspiration/img9.jpeg',
  '/images/inspiration/img10.jpeg',
  '/images/inspiration/img11.jpeg',
  '/images/inspiration/img12.jpeg',
  '/images/inspiration/img13.png',
];

const AnimatedCard = ({ children, delay = 0 }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay,
      },
    },
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={cardVariants}
    >
      {children}
    </motion.div>
  );
};

const ContentCreationPage: React.FC = () => {
  const [visibleImages, setVisibleImages] = useState(10);

  const loadMoreImages = () => {
    setVisibleImages(prev => prev + 2);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-900 dark:via-slate-900 dark:to-gray-900 text-gray-900 dark:text-white">
      {/* Compact Header */}
      <div className="backdrop-blur-2xl bg-white/60 dark:bg-slate-900/60 border-b border-slate-200/50 dark:border-slate-700/30">
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <motion.div
            className="flex items-center justify-between"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div>
              <h1 className="text-2xl sm:text-3xl font-black bg-clip-text text-transparent" style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                Content Hub
              </h1>

            </div>

            <div className="hidden sm:flex items-center space-x-4 text-xs text-slate-500 dark:text-slate-400">
            </div>
          </motion.div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Square Studio Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-4"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group aspect-square"
              >
                <Link to={service.link} className="block w-full h-full">
                  <div className="relative w-full h-full">
                    {/* Glow Effect */}
                    <div className={`absolute -inset-1 bg-gradient-to-r ${service.gradient} rounded-3xl blur opacity-0 group-hover:opacity-30 transition-all duration-500`}></div>

                    <div className={`relative w-full h-full overflow-hidden rounded-3xl bg-white/90 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 shadow-2xl group-hover:${service.glowColor} transition-all duration-500`}>

                      {/* Background Media */}
                      <div className={`absolute inset-0 ${service.title === '3D Studio' ? 'brightness-125 contrast-110' : ''}`}>
                        {service.isVideo ? (
                          <VideoCollage videoUrls={service.videoUrls} title={service.title} />
                        ) : service.isCollage ? (
                          <ImageCollage imageUrls={service.imageUrls} title={service.title} />
                        ) : (
                          <img src={service.imageUrl} alt={service.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                        )}

                      </div>

                      {/* Content Overlay */}
                      <div className="relative z-10 h-full flex flex-col">
                        {/* Top Section - Arrow */}
                        <div className="flex justify-end p-4">
                          <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-4 group-hover:translate-x-0">
                            <ArrowRight className="w-5 h-5 text-white" />
                          </div>
                        </div>

                        {/* Spacer to push content to bottom */}
                        <div className="flex-1"></div>

                        {/* Bottom Section - Hover details only */}
                        <div className="p-6">
                          {/* Details - Show on hover */}
                          <div className="opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                            <p className="text-white/90 text-sm mb-3">
                              {service.description}
                            </p>

                            <div className="flex flex-wrap gap-2 mb-4">
                              {service.features.map((feature, featureIndex) => (
                                <span key={featureIndex} className="px-2 py-1 bg-white/20 text-white/90 text-xs rounded-md">
                                  {feature}
                                </span>
                              ))}
                            </div>

                            {/* Launch Button */}
                            <div className={`w-full py-2 px-4 bg-white/20 backdrop-blur-sm text-white rounded-xl font-semibold text-center text-sm border border-white/30 hover:bg-white/30 transition-all duration-300`}>
                              Launch Studio
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>

                {/* Service Title and Subtitle - Moved up closer to subheader */}
                <motion.div
                  className="text-center mt-3"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.9 + index * 0.1 }}
                >
                  <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 mb-1 flex items-center justify-center space-x-2">
                    <span>{service.title}</span>
                    {service.isNew && (
                      <span className="px-2 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold rounded-full animate-pulse">
                        NEW
                      </span>
                    )}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">
                    {service.subtitle}
                  </p>
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Inspiration Gallery - Pushed down with much more spacing */}
        <motion.div
          className="mt-40"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <div className="text-center mb-16">
            <div className="relative inline-block">
              <div className="absolute -inset-4 bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-cyan-500/10 rounded-3xl blur-2xl"></div>
              <div className="relative flex items-center justify-center space-x-4 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/25">
                  <Palette className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-black bg-gradient-to-r from-gray-900 via-emerald-600 to-teal-600 bg-clip-text text-transparent dark:from-white dark:via-emerald-300 dark:to-teal-300">
                  Creative Inspiration
                </h2>
              </div>
              <p className="text-lg text-slate-600 dark:text-slate-300 font-medium">
                Explore trending designs and discover your next creative breakthrough
              </p>
            </div>
          </div>

          <div className="columns-2 md:columns-3 lg:columns-4 gap-6 space-y-6">
            {inspirationImages.slice(0, visibleImages).map((src, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.03 }}
                className="group break-inside-avoid"
              >
                <div className="relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl bg-white/90 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 hover:shadow-emerald-500/20 transition-all duration-500 transform hover:scale-[1.02] hover:-translate-y-2">
                  <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-cyan-500/10 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                  <div className="relative">
                    <img
                      src={src}
                      alt={`Creative inspiration ${index + 1}`}
                      className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                            <Eye className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-white text-sm font-semibold">Explore</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="bg-emerald-500/20 backdrop-blur-sm text-white hover:bg-emerald-500/30 rounded-xl border border-white/20"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {visibleImages < inspirationImages.length && (
            <motion.div
              className="text-center mt-16"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.8 }}
            >
              <Button
                onClick={loadMoreImages}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-emerald-500/25 text-base"
              >
                <Layers className="w-5 h-5 mr-2" />
                Load More Inspiration
              </Button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ContentCreationPage;